import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadUserData(currentUser.uid);
      } else {
        navigate('/auth');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadUserData = async (userId) => {
    try {
      // Load user profile
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        setUserProfile(userDoc.data());
      }

      // Load user campaigns (mock data for now since we don't have real campaigns)
      // In a real app, you'd query campaigns collection where userId === currentUser.uid
      const mockCampaigns = [
        {
          id: '1',
          name: 'Pacific Beach Summer Campaign',
          status: 'active',
          area: 'Pacific Beach',
          sent: 1250,
          delivered: 1127,
          opened: 1062,
          responded: 144,
          createdAt: '2024-01-15',
          budget: 2500,
          roi: 287
        },
        {
          id: '2',
          name: 'Mission Valley Holiday Promo',
          status: 'completed',
          area: 'Mission Valley',
          sent: 890,
          delivered: 856,
          opened: 807,
          responded: 98,
          createdAt: '2023-12-01',
          budget: 1800,
          roi: 245
        },
        {
          id: '3',
          name: 'Hillcrest Local Business Drive',
          status: 'active',
          area: 'Hillcrest',
          sent: 650,
          delivered: 627,
          opened: 591,
          responded: 73,
          createdAt: '2024-01-20',
          budget: 1300,
          roi: 198
        }
      ];
      
      setCampaigns(mockCampaigns);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const calculateTotalMetrics = () => {
    const totals = campaigns.reduce((acc, campaign) => {
      acc.sent += campaign.sent;
      acc.delivered += campaign.delivered;
      acc.opened += campaign.opened;
      acc.responded += campaign.responded;
      acc.budget += campaign.budget;
      return acc;
    }, { sent: 0, delivered: 0, opened: 0, responded: 0, budget: 0 });

    const openRate = totals.delivered > 0 ? (totals.opened / totals.delivered * 100) : 0;
    const responseRate = totals.delivered > 0 ? (totals.responded / totals.delivered * 100) : 0;
    const avgROI = campaigns.length > 0 ? campaigns.reduce((sum, c) => sum + c.roi, 0) / campaigns.length : 0;
    
    return {
      ...totals,
      openRate: openRate.toFixed(1),
      responseRate: responseRate.toFixed(1),
      avgROI: avgROI.toFixed(0)
    };
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="bg-gradient"></div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ color: 'white', fontSize: '1.2rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  const metrics = calculateTotalMetrics();
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const completedCampaigns = campaigns.filter(c => c.status === 'completed').length;

  return (
    <div className="dashboard-container">
      <div className="bg-gradient"></div>
      
      <nav className="nav">
        <div className="nav-content">
          <div className="logo" onClick={() => navigate('/')}>San Diego Mailers</div>
          <div className="nav-links">
            <span className="nav-link">
              Welcome, {userProfile?.firstName || user?.displayName || 'User'}
            </span>
            <button className="nav-link" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <span className="nav-link">
              Welcome, {userProfile?.firstName || user?.displayName || 'User'}
            </span>
            <button className="nav-link" onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}>
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Medical Practice Dashboard</h1>
          <p className="dashboard-subtitle">
            Track your patient acquisition campaigns and practice growth in real-time
          </p>
        </div>

        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-title">Total Delivered</div>
            <div className="metric-value">{metrics.delivered.toLocaleString()}</div>
            <div className="metric-change">
              {metrics.sent > 0 ? `${((metrics.delivered / metrics.sent) * 100).toFixed(1)}% delivery rate` : 'No campaigns yet'}
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Open Rate</div>
            <div className="metric-value">{metrics.openRate}%</div>
            <div className="metric-change">
              {metrics.opened.toLocaleString()} total opens
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Response Rate</div>
            <div className="metric-value">{metrics.responseRate}%</div>
            <div className="metric-change">
              {metrics.responded.toLocaleString()} total responses
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Average ROI</div>
            <div className="metric-value">{metrics.avgROI}%</div>
            <div className="metric-change">
              ${metrics.budget.toLocaleString()} total budget
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Active Campaigns</div>
            <div className="metric-value">{activeCampaigns}</div>
            <div className="metric-change">
              {completedCampaigns} completed
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Total Campaigns</div>
            <div className="metric-value">{campaigns.length}</div>
            <div className="metric-change">
              Since {userProfile?.createdAt ? new Date(userProfile.createdAt).getFullYear() : 'signup'}
            </div>
          </div>
        </div>

        <div className="campaigns-section">
          <div className="section-header">
            <h2 className="section-title">Recent Campaigns</h2>
            <button className="cta-primary">
              Create New Campaign
            </button>
          </div>
          
          <div className="campaigns-list">
            {campaigns.length > 0 ? (
              campaigns.map(campaign => (
                <div key={campaign.id} className="campaign-item">
                  <div className="campaign-info">
                    <h3>{campaign.name}</h3>
                    <p>{campaign.area} • {campaign.delivered.toLocaleString()} delivered • {((campaign.opened / campaign.delivered) * 100).toFixed(1)}% open rate</p>
                    <div className="campaign-metrics">
                      <span>ROI: {campaign.roi}%</span>
                      <span>Budget: ${campaign.budget.toLocaleString()}</span>
                      <span>Responses: {campaign.responded}</span>
                    </div>
                  </div>
                  <div className="campaign-actions">
                    <span className={`campaign-status ${campaign.status === 'active' ? 'status-active' : 'status-completed'}`}>
                      {campaign.status}
                    </span>
                    <button className="campaign-action-btn">View Details</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <h3>No campaigns yet</h3>
                <p>Create your first direct mail campaign to get started</p>
                <button className="cta-primary" style={{ marginTop: '16px' }}>
                  Create Your First Campaign
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
